import CheckIcon from "@mui/icons-material/Check";
import type { TableColumn, TableOptions } from "../../../types/table";
import type { AccountDialogProps, FactorRow } from "../../../types/account-ui";
import DeleteIcon from "@mui/icons-material/Delete";
import { Grid } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { BarLoader } from "react-spinners";

import Table from "../../components/table";
import converterService from "../../services/converter";
import webauthnService from "../../services/webauthn";
import SecondFactorOverview from "./second-factor-overview";

const useStyles = makeStyles((theme) => ({
	textField: {
		width: "100%",
	},
}));
const MultifactorAuthenticatorWebauthn = (props: AccountDialogProps) => {
	const { open, onClose } = props;
	const { t } = useTranslation();
	const classes = useStyles();
	const [title, setTitle] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [view, setView] = React.useState<"default" | "create_step0">("default");
	const [webauthns, setWebauthns] = React.useState<FactorRow[] | null>(null);
	const [listError, setListError] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);

	React.useEffect(() => {
		loadWebauthns();
	}, []);

	const loadWebauthns = () => {
		webauthnService.readWebauthn().then(
			(webauthns) => {
				setListError(false);
				setWebauthns(
					webauthns!.map((webauthn): FactorRow => {
						return [webauthn.id, webauthn.title, webauthn.active];
					}),
				);
			},
			(error) => {
				setListError(true);
				console.log(error);
			},
		);
	};
	const create = () => {
		setLoading(true);

		webauthnService.createWebauthn(title).then(
			async (result) => {
				const webauthn = result!;
				setTitle("");

				// The server sends binary strings; this view replaces them in place
				// before passing the same options object to the browser.
				const options: Omit<
					PublicKeyCredentialCreationOptions,
					"challenge" | "user" | "excludeCredentials"
				> & {
					challenge: string | BufferSource;
					user: Omit<PublicKeyCredentialUserEntity, "id"> & {
						id: string | BufferSource;
					};
					excludeCredentials?: typeof webauthn.options.excludeCredentials;
				} = webauthn.options;
				options.challenge = Uint8Array.from(webauthn.options.challenge, (c) =>
					c.charCodeAt(0),
				);
				options.user.id = Uint8Array.from(webauthn.options.user.id, (c) =>
					c.charCodeAt(0),
				);

				let credential: PublicKeyCredential;
				try {
					credential = (await navigator.credentials.create({
						publicKey: options as PublicKeyCredentialCreationOptions,
					})) as PublicKeyCredential;
				} catch (error) {
					setLoading(false);
					setView("default");
					return;
				}

				const onSuccess = async () => {
					await loadWebauthns();
					setLoading(false);
					setView("default");
				};

				const onError = (error: unknown) => {
					console.log(error);
					setLoading(false);
				};

				const response =
					credential.response as AuthenticatorAttestationResponse;
				const convertedCredential = {
					id: credential.id,
					rawId: credential.id,
					type: credential.type,
					authenticatorAttachment: credential.authenticatorAttachment,
					response: {
						attestationObject: converterService.arrayBufferToBase64(
							response.attestationObject,
						),
						clientDataJSON: converterService.arrayBufferToBase64(
							response.clientDataJSON,
						),
					},
				};

				return webauthnService
					.activateWebauthn(webauthn.id, JSON.stringify(convertedCredential))
					.then(onSuccess, onError);
			},
			(error) => {
				console.log(error);
			},
		);
	};
	const onDelete = (rowData: FactorRow) => {
		setErrors([]);

		const onSuccess = () => {
			loadWebauthns();
		};

		const onError = (error: unknown) => {
			console.log(error);
		};

		return webauthnService.deleteWebauthn(rowData[0]).then(onSuccess, onError);
	};
	const onCreate = () => {
		setView("create_step0");
	};

	const columns: TableColumn<FactorRow>[] = [
		{ name: t("ID"), options: { display: false } },
		{ name: t("FIDO2_WEBAUTHN_TITLE") },
		{
			name: t("ACTIVE"),
			options: {
				filter: true,
				sort: true,
				empty: false,
				customBodyRender: (value, tableMeta, updateValue) => {
					return <span>{tableMeta.rowData[2] && <CheckIcon />}</span>;
				},
			},
		},
		{
			name: t("DELETE"),
			options: {
				filter: true,
				sort: false,
				empty: false,
				customHeadLabelRender: () => null,
				customBodyRender: (value, tableMeta, updateValue) => {
					return (
						<IconButton
							onClick={() => {
								onDelete(tableMeta.rowData);
							}}
							size="large"
						>
							<DeleteIcon />
						</IconButton>
					);
				},
			},
		},
	];

	const options: TableOptions = {
		filterType: "checkbox",
	};

	return (
		<Dialog
			fullWidth
			maxWidth={"sm"}
			open={open}
			onClose={() => {
				setView("default");
				onClose();
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">{t("FIDO2_WEBAUTHN")}</DialogTitle>
			{view === "default" && (
				<SecondFactorOverview
					rows={webauthns}
					loadError={listError}
					onCreate={onCreate}
				>
					<Table
						data={webauthns || []}
						columns={columns}
						options={options}
						onCreate={onCreate}
					/>
				</SecondFactorOverview>
			)}
			{view === "create_step0" && (
				<DialogContent>
					<Grid container>
						<Grid item xs={12} sm={12} md={12}>
							<TextField
								className={classes.textField}
								variant="outlined"
								margin="dense"
								size="small"
								id="title"
								label={t("TITLE")}
								name="title"
								autoComplete="off"
								required
								value={title}
								onChange={(event) => {
									setTitle(event.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12}>
							<Button
								variant="contained"
								color="primary"
								onClick={create}
								disabled={!title || loading}
							>
								<span style={!loading ? {} : { display: "none" }}>
									{t("CREATE")}
								</span>
								<BarLoader
									color={"#FFF"}
									height={17}
									width={37}
									loading={loading}
								/>
							</Button>
						</Grid>
					</Grid>
				</DialogContent>
			)}
			<DialogActions>
				<Button
					onClick={() => {
						setView("default");
						onClose();
					}}
					autoFocus
				>
					{t("CLOSE")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

MultifactorAuthenticatorWebauthn.propTypes = {
	onClose: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
};

export default MultifactorAuthenticatorWebauthn;
